import JsBarcode from "jsbarcode";
import { clampLabelCount, type LabelContent, type LabelSize } from "@hisense/shared";

const escapeHtml = (text: string) => text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
interface BarcodeData { encodings?: Array<{ data: string }>; }
/** JsBarcode documents plain-object output; its community types only cover DOM targets. */
const encodeBarcode = JsBarcode as unknown as (target: BarcodeData, text: string, options: { format: "CODE128"; displayValue: boolean; margin: number }) => void;
/**
 * Chuỗi bit Code128 thô — một bit là MỘT module của mã vạch.
 *
 * Tách riêng vì bản xem trước tem cần đặt từng module đúng bề rộng máy in
 * dùng (tham số narrow của lệnh BARCODE), chứ không phải kéo giãn một SVG đã
 * dựng sẵn cho vừa khung.
 */
export function labelBarcodeBits(value: string): string | null {
  try {
    const result: BarcodeData = {};
    encodeBarcode(result, value, { format: "CODE128", displayValue: false, margin: 0 });
    const bits = result.encodings?.map((part) => part.data).join("");
    return bits && /^[01]+$/.test(bits) ? bits : null;
  } catch { return null; }
}

export function labelBarcode(value: string): { svg: string; modules: number } | null {
  try {
    const bits = labelBarcodeBits(value);
    if (!bits) return null;
    const bars: string[] = [];
    for (let i = 0; i < bits.length;) {
      if (bits[i] === "0") { i++; continue; }
      const start = i;
      while (bits[i] === "1") i++;
      bars.push(`<rect x="${start + 10}" y="0" width="${i - start}" height="40"/>`);
    }
    return { modules: bits.length + 20,
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${bits.length + 20} 40" preserveAspectRatio="none" role="img" aria-label="${escapeHtml(value)}"><g fill="black">${bars.join("")}</g></svg>` };
  } catch { return null; }
}

/** CT321D is 203 dpi (8 dots/mm); keep at least one physical dot per module. */
export function labelBarcodeFits(size: LabelSize, barcode?: string | null): boolean {
  if (!barcode) return true;
  const encoded = labelBarcode(barcode);
  return !!encoded && encoded.modules <= Math.floor((size.widthMm - 4) * 8);
}

/** Tiêu đề các dòng trên tem — "Desc" / "Color"… như hệ cũ; bản chữ Hoa cho tem chọn in tiếng Hoa. */
const LABEL_CAPTIONS = {
  en: { desc: "Desc", color: "Color", size: "Size", qty: "Sample Qty", lot: "LOT" },
  zh: { desc: "描述", color: "颜色", size: "尺寸", qty: "样品数量", lot: "批号" },
} as const;

export function buildLabelDocument(size: LabelSize, contents: LabelContent[]): string {
  const labels = contents.map((content) => {
    const barcode = content.barcode ? labelBarcode(content.barcode) : null;
    // Tiêu đề dòng theo ngôn ngữ chọn lúc in (12/10/2026): tem tiếng Hoa không lẫn chữ Anh ở tiêu đề, tem tiếng Anh / "cả hai" giữ như cũ.
    const cap = content.language === "zh" ? LABEL_CAPTIONS.zh : LABEL_CAPTIONS.en;
    // "Cả hai": mô tả Anh và Hoa mỗi bản MỘT dòng — tổng vẫn hai dòng như mô tả thường, không đẩy mã vạch ra khỏi tem.
    const descRows: [string, string | null | undefined, string][] = content.descriptionZh
      ? [[cap.desc, content.description, "one"], [LABEL_CAPTIONS.zh.desc, content.descriptionZh, "one"]]
      : [[cap.desc, content.description, "two"]];
    const rows: [string, string | null | undefined, string][] = [
      ...descRows, [cap.color, content.color, "one"],
      [cap.size, content.size, "two"], [cap.qty, content.sampleQty, "one"], [cap.lot, content.lotNumber, "one"],
    ];
    const one = `<article class="label"><div class="title">${escapeHtml(content.productRef)}</div>${rows.filter(([,value]) => value?.trim()).map(([label,value,lines]) => `<div class="line ${lines}">${label}: ${escapeHtml(value!)}</div>`).join("")}${barcode ? `<div class="barcode">${barcode.svg}</div>` : ""}${content.barcode ? `<div class="code">${escapeHtml(content.barcode)}</div>` : ""}${content.footer ? `<div class="foot">${escapeHtml(content.footer)}</div>` : ""}</article>`;
    return one.repeat(clampLabelCount(content.count));
  }).join("");
  /**
   * Xoay 90°: TRANG đổi chiều (cao × rộng), còn khung tem giữ nguyên
   * `widthMm × heightMm` rồi quay vào trong. `rotate(90deg)` quanh góc trên
   * trái đưa khung ra ngoài mép trái, nên đẩy lại đúng một bề rộng trang
   * (`heightMm`) để nó nằm gọn trong trang. Bố cục bên trong không phải biết
   * gì về chuyện này — chữ và mã vạch vẫn đo theo tem thật.
   */
  const pageCss = size.rotate
    ? `@page {size:${size.heightMm}mm ${size.widthMm}mm;margin:0}`
    : `@page {size:${size.widthMm}mm ${size.heightMm}mm;margin:0}`;
  const rotateCss = size.rotate
    ? `transform:translateX(${size.heightMm}mm) rotate(90deg);transform-origin:top left;`
    : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(contents[0]?.productRef ?? "")}</title><style>
${pageCss}*{box-sizing:border-box}body{margin:0;font-family:Arial,"Helvetica Neue",sans-serif;color:black;background:white}
.label{width:${size.widthMm}mm;height:${size.heightMm}mm;padding:1.5mm 2mm;overflow:hidden;display:flex;flex-direction:column;gap:.35mm;break-after:page;page-break-after:always;${rotateCss}}.label:last-child{break-after:auto;page-break-after:auto}
.title{font-size:3mm;font-weight:bold;line-height:1.1;flex-shrink:0}.line{font-size:2.15mm;line-height:1.15;flex-shrink:0;display:-webkit-box;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere}.one{-webkit-line-clamp:1}.two{-webkit-line-clamp:2}.barcode{height:4mm;min-height:4mm;margin-top:auto}.barcode svg{display:block;width:100%;height:100%}.code{font-family:monospace;font-size:1.8mm;line-height:1;flex-shrink:0;white-space:nowrap}.foot{font-size:1.7mm;line-height:1.1;flex-shrink:0;overflow:hidden;white-space:nowrap}
</style></head><body>${labels}</body></html>`;
}
